import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Plus, ArrowLeft, Send, Clock, CheckCircle, XCircle, AlertCircle, Upload, X, User, MessageSquare, Settings, Bell, BellOff, UserPlus, Trash2, Mail, Edit3, Save } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser } from '../services/auth';

const CATEGORIES = ['Website Issue', 'Equipment Issue', 'Internal Issue', 'Admin Issue', 'Other'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];
const STATUSES = ['All', 'Open', 'Assigned to Me', 'In Progress', 'Resolved', 'Closed'];

const INPUT = { padding: '7px 12px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
const TEXTAREA = { ...INPUT, minHeight: 80, resize: 'vertical' };
const BTN = (bg, disabled) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 14px',
  background: disabled ? 'var(--gray-300)' : bg, color: '#fff', border: 'none', borderRadius: 6,
  fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
});
const PRIORITY_COLORS = { Critical: '#dc2626', High: '#EA733D', Medium: '#00395C', Low: '#6b7280' };
const STATUS_COLORS = { Open: '#16a34a', 'In Progress': '#0e7490', Resolved: '#15803d', Closed: '#6b7280' };
const STATUS_ICONS = { Open: AlertCircle, 'In Progress': Clock, Resolved: CheckCircle, Closed: XCircle };

export default function TroubleTickets({ initialTicketId }) {
  const user = getCurrentUser();
  const userRoles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map(r => r.trim());
  const isAllowed = userRoles.includes('Admin') || userRoles.includes('IT') || userRoles.includes('Manager');
  const isAdmin = userRoles.includes('Admin') || userRoles.includes('IT');

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | detail | new | settings
  const [statusFilter, setStatusFilter] = useState('Open');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketUpdates, setTicketUpdates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // New ticket form
  const [form, setForm] = useState({ title: '', category: 'Website Issue', priority: 'Medium', description: '', customer_os: '', customer_browser: '', customer_info: '', recommended_steps: '', assigned_to: '' });
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  // Detail view - add note
  const [noteText, setNoteText] = useState('');
  const [noteStatus, setNoteStatus] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Users for assignment (from ticket_recipients, fallback to adm_users)
  const [adminUsers, setAdminUsers] = useState([]);
  const [lightboxImg, setLightboxImg] = useState(null);

  // Settings view
  const [recipients, setRecipients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [addUserSelect, setAddUserSelect] = useState('');
  const [editingEmail, setEditingEmail] = useState(null);
  const [editEmailVal, setEditEmailVal] = useState('');

  const [allTickets, setAllTickets] = useState([]);
  const [search, setSearch] = useState('');

  const loadTickets = useCallback(async () => {
    try {
      const data = await neonQuery('tickets_list', { status: statusFilter, user_id: user?.id });
      setTickets(data || []);
    } catch (e) { console.error('Failed to load tickets:', e); }
    finally { setLoading(false); }
  }, [statusFilter, user?.id]);

  const loadAllCounts = useCallback(async () => {
    try {
      const data = await neonQuery('tickets_list', { status: 'All' });
      setAllTickets(data || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { if (isAllowed) { loadTickets(); loadAllCounts(); } }, [loadTickets, isAllowed, loadAllCounts]);

  // Auto-open ticket from URL param (e.g. email deep link)
  useEffect(() => {
    if (initialTicketId && isAllowed) {
      openTicket(initialTicketId);
      // Clear URL params so refresh doesn't re-trigger
      if (window.location.search) window.history.replaceState({}, '', window.location.pathname);
    }
  }, [initialTicketId, isAllowed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load assignable users: prefer ticket_recipients, fallback to adm_users Admin/Manager
  const loadAssignableUsers = useCallback(async () => {
    try {
      const recipRows = await neonQuery('ticket_recipients_list', {});
      const assignable = (recipRows || []).filter(r => r.can_be_assigned);
      if (assignable.length > 0) {
        setAdminUsers(assignable.map(r => ({ admin_id: r.admin_id, staff_name: r.staff_name })));
        return;
      }
    } catch(e) { /* fall through */ }
    // Fallback
    try {
      const rows = await neonQuery('users_list');
      const admins = (rows || []).filter(u => {
        const roles = Array.isArray(u.roles) ? u.roles : (u.roles || '').split(',').map(r => r.trim());
        return (roles.includes('Admin') || roles.includes('Manager')) && u.is_active !== '0';
      });
      setAdminUsers(admins);
    } catch(e) {}
  }, []);

  useEffect(() => { if (isAllowed) loadAssignableUsers(); }, [isAllowed, loadAssignableUsers]);

  if (!isAllowed) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>
        <AlertTriangle size={48} style={{ marginBottom: 12, color: 'var(--orange)' }} />
        <h2 style={{ color: 'var(--navy)' }}>Access Restricted</h2>
        <p>Trouble Tickets is only available to Managers and Admins.</p>
      </div>
    );
  }

  const handleScreenshot = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Resize to max 1200px wide to keep payload small
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX_W = 1200;
        let w = img.width, h = img.height;
        if (w > MAX_W) { h = Math.round(h * MAX_W / w); w = MAX_W; }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setScreenshot(dataUrl);
        setScreenshotPreview(dataUrl);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const removeScreenshot = () => { setScreenshot(null); setScreenshotPreview(null); };

  const submitTicket = async () => {
    setError('');
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (!form.description.trim()) { setError('Description is required.'); return; }
    if (!form.assigned_to) { setError('Please assign this ticket to someone.'); return; }
    const assignUser = adminUsers.find(u => String(u.admin_id) === String(form.assigned_to));
    setSaving(true);
    try {
      const data = await neonQuery('tickets_create', {
        ...form,
        assigned_to: form.assigned_to,
        assigned_to_name: assignUser?.staff_name || null,
        screenshot: screenshot || null,
        created_by: user?.id,
        created_by_name: user?.staffName,
      });
      if (data?.ticket) {
        setView('list');
        resetForm();
        loadTickets();
      }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const resetForm = () => {
    setForm({ title: '', category: 'Website Issue', priority: 'Medium', description: '', customer_os: '', customer_browser: '', customer_info: '', recommended_steps: '', assigned_to: '' });
    setScreenshot(null);
    setScreenshotPreview(null);
    setError('');
  };

  const openTicket = async (ticketId) => {
    try {
      const data = await neonQuery('tickets_get', { ticketId });
      if (data?.ticket) {
        setSelectedTicket(data.ticket);
        setTicketUpdates(data.updates || []);
        setView('detail');
        setNoteText('');
        setNoteStatus('');
      }
    } catch (e) { console.error(e); }
  };

  const addNote = async () => {
    if (!noteText.trim() && !noteStatus) return;
    setAddingNote(true);
    try {
      const data = await neonQuery('tickets_add_note', {
        ticketId: selectedTicket.id,
        note: noteText.trim() || `Status changed to ${noteStatus}`,
        newStatus: noteStatus || null,
        created_by: user?.id,
        created_by_name: user?.staffName,
        submitted_by_id: selectedTicket.created_by,
        submitted_by_name: selectedTicket.created_by_name,
      });
      if (data?.ticket) {
        setSelectedTicket(data.ticket);
        setTicketUpdates(data.updates || []);
        setNoteText('');
        setNoteStatus('');
      }
    } catch (e) { console.error(e); }
    finally { setAddingNote(false); }
  };

  const assignTicket = async (assignTo) => {
    const assignUser = adminUsers.find(u => String(u.admin_id) === String(assignTo));
    try {
      const data = await neonQuery('tickets_update', {
        ticketId: selectedTicket.id,
        assigned_to: assignTo || null,
        assigned_to_name: assignUser?.staff_name || null,
        assigner_id: user?.id,
        assigner_name: user?.staffName,
      });
      if (data?.ticket) setSelectedTicket(data.ticket);
    } catch (e) { console.error(e); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  const timeSince = (d) => {
    if (!d) return '';
    const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
    return `${Math.floor(mins / 1440)}d ago`;
  };

  // ── Settings functions ──
  const loadRecipients = async () => {
    try {
      const rows = await neonQuery('ticket_recipients_list', {});
      setRecipients(rows || []);
    } catch(e) { console.error(e); }
  };

  const loadAllUsers = async () => {
    try {
      const rows = await neonQuery('users_list');
      setAllUsers((rows || []).filter(u => u.is_active !== '0'));
    } catch(e) {}
  };

  const addRecipient = async () => {
    if (!addUserSelect) return;
    const u = allUsers.find(u => String(u.admin_id) === addUserSelect);
    if (!u) return;
    try {
      const rows = await neonQuery('ticket_recipients_add', {
        admin_id: u.admin_id, staff_name: u.staff_name, email: u.email || '',
        receives_notifications: true, can_be_assigned: true,
      });
      setRecipients(rows || []);
      setAddUserSelect('');
      loadAssignableUsers();
    } catch(e) { console.error(e); }
  };

  const updateRecipient = async (id, field, value) => {
    try {
      const rows = await neonQuery('ticket_recipients_update', { id, [field]: value });
      setRecipients(rows || []);
      if (field === 'can_be_assigned') loadAssignableUsers();
    } catch(e) { console.error(e); }
  };

  const removeRecipient = async (id) => {
    try {
      const rows = await neonQuery('ticket_recipients_remove', { id });
      setRecipients(rows || []);
      loadAssignableUsers();
    } catch(e) { console.error(e); }
  };

  const saveEmail = async (id) => {
    await updateRecipient(id, 'email', editEmailVal);
    setEditingEmail(null);
    setEditEmailVal('');
  };

  // ── SETTINGS VIEW ──
  if (view === 'settings') {
    const existingIds = new Set(recipients.map(r => String(r.admin_id)));
    const availableUsers = allUsers.filter(u => !existingIds.has(String(u.admin_id)));

    return (
      <div style={{ padding: '0 8px', maxWidth: 800 }}>
        <button style={{ ...BTN('transparent', false), color: 'var(--navy)', padding: '4px 0', marginBottom: 12 }} onClick={() => setView('list')}>
          <ArrowLeft size={14} /> Back to Tickets
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <Settings size={20} color="var(--navy)" />
          <h2 style={{ margin: 0, color: 'var(--navy)', fontSize: 18 }}>Ticket Settings</h2>
        </div>

        {/* Add user */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header" style={{ fontSize: 13, fontWeight: 700 }}>
            <UserPlus size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
            Add Team Member
          </div>
          <div className="card-body">
            <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 10px 0' }}>
              Add users who should receive ticket notifications or be assignable to tickets.
              {recipients.length === 0 && <span style={{ color: 'var(--orange)', fontWeight: 600 }}> No recipients configured yet — notifications currently go to all Admins/Managers.</span>}
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select style={{ ...INPUT, width: 'auto', minWidth: 220 }} value={addUserSelect} onChange={e => setAddUserSelect(e.target.value)}>
                <option value="">Select a user...</option>
                {availableUsers.map(u => (
                  <option key={u.admin_id} value={u.admin_id}>{u.staff_name}{u.email ? ` (${u.email})` : ''}</option>
                ))}
              </select>
              <button style={BTN('var(--navy)', !addUserSelect)} onClick={addRecipient} disabled={!addUserSelect}>
                <UserPlus size={12} /> Add
              </button>
            </div>
          </div>
        </div>

        {/* Recipients list */}
        <div className="card">
          <div className="card-header" style={{ fontSize: 13, fontWeight: 700 }}>
            <User size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
            Ticket Team ({recipients.length})
          </div>
          <div className="card-body" style={{ padding: recipients.length ? 0 : undefined }}>
            {recipients.length === 0 ? (
              <div style={{ color: 'var(--gray-400)', fontSize: 12 }}>
                No team members configured. Add users above to control who gets ticket notifications and assignments.
              </div>
            ) : (
              <table className="orders-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th style={{ textAlign: 'center', width: 100 }}>Notifications</th>
                    <th style={{ textAlign: 'center', width: 100 }}>Assignable</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600, color: 'var(--navy)' }}>{r.staff_name}</td>
                      <td>
                        {editingEmail === r.id ? (
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <input style={{ ...INPUT, width: 200 }} value={editEmailVal}
                              onChange={e => setEditEmailVal(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && saveEmail(r.id)}
                              placeholder="email@example.com" autoFocus />
                            <button style={{ ...BTN('#16a34a', false), padding: '5px 8px' }} onClick={() => saveEmail(r.id)}>
                              <Save size={11} />
                            </button>
                            <button style={{ ...BTN('var(--gray-400)', false), padding: '5px 8px' }} onClick={() => { setEditingEmail(null); setEditEmailVal(''); }}>
                              <X size={11} />
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 12, color: r.email ? 'var(--gray-700)' : 'var(--gray-400)' }}>
                              {r.email || 'No email set'}
                            </span>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 2 }}
                              onClick={() => { setEditingEmail(r.id); setEditEmailVal(r.email || ''); }}
                              title="Edit email">
                              <Edit3 size={11} />
                            </button>
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: r.receives_notifications ? '#16a34a' : 'var(--gray-300)' }}
                          onClick={() => updateRecipient(r.id, 'receives_notifications', !r.receives_notifications)}
                          title={r.receives_notifications ? 'Receiving notifications — click to mute' : 'Muted — click to enable notifications'}>
                          {r.receives_notifications ? <Bell size={16} /> : <BellOff size={16} />}
                        </button>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: r.can_be_assigned ? 'var(--navy)' : 'var(--gray-300)' }}
                          onClick={() => updateRecipient(r.id, 'can_be_assigned', !r.can_be_assigned)}
                          title={r.can_be_assigned ? 'Can be assigned tickets — click to disable' : 'Cannot be assigned — click to enable'}>
                          <User size={16} />
                        </button>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--gray-300)' }}
                          onClick={() => { if (window.confirm(`Remove ${r.staff_name} from ticket team?`)) removeRecipient(r.id); }}
                          title="Remove from ticket team">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div style={{ marginTop: 16, padding: 14, background: 'var(--gray-50)', borderRadius: 8, border: '1px solid var(--gray-200)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>How it works</div>
          <ul style={{ fontSize: 12, color: 'var(--gray-600)', margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
            <li><strong>Notifications:</strong> When a new ticket is submitted, email alerts go to all team members with the bell icon enabled.</li>
            <li><strong>Assignable:</strong> Team members with the person icon enabled appear in the "Assigned To" dropdown on tickets.</li>
            <li><strong>Fallback:</strong> If no team members are configured here, notifications go to all Admins and Managers with email addresses.</li>
          </ul>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ──
  if (view === 'list') {
    const q = search.trim().toLowerCase();
    const filteredTickets = q
      ? tickets.filter(t => String(t.id).includes(q) || (t.title || '').toLowerCase().includes(q))
      : tickets;
    return (
      <div style={{ padding: '0 8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={20} color="var(--orange)" />
            <h2 style={{ margin: 0, color: 'var(--navy)', fontSize: 18 }}>Trouble Tickets</h2>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {isAdmin && (
              <button style={{ ...BTN('transparent', false), color: 'var(--gray-500)', border: '1px solid var(--gray-300)', padding: '7px 10px' }}
                onClick={() => { setView('settings'); loadRecipients(); loadAllUsers(); }}
                title="Ticket Settings">
                <Settings size={14} />
              </button>
            )}
            <button style={BTN('var(--orange)', false)} onClick={() => { resetForm(); setView('new'); }}>
              <Plus size={14} /> New Ticket
            </button>
          </div>
        </div>

        {/* Search */}
        <div style={{ marginBottom: 12 }}>
          <input
            style={{ ...INPUT, maxWidth: 320 }}
            placeholder="Search by # or title..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Status filter tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
          {STATUSES.map(s => {
            const count = s === 'All' ? allTickets.length
              : s === 'Assigned to Me' ? allTickets.filter(t => String(t.assigned_to) === String(user?.id) && t.status !== 'Closed' && t.status !== 'Resolved').length
              : allTickets.filter(t => t.status === s).length;
            const active = statusFilter === s;
            return (
              <button key={s} onClick={() => { setStatusFilter(s); setLoading(true); }}
                style={{ padding: '5px 14px', borderRadius: 20, border: active ? 'none' : '1px solid var(--gray-300)', background: active ? 'var(--navy)' : '#fff', color: active ? '#fff' : 'var(--gray-600)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                {s} {count > 0 && <span style={{ opacity: 0.7 }}>({count})</span>}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>Loading tickets...</div>
        ) : filteredTickets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>
            {search.trim() ? `No tickets match "${search.trim()}".` : (<>No tickets {statusFilter !== 'All' ? `with status "${statusFilter}"` : 'yet'}. Click "New Ticket" to report an issue.</>)}
          </div>
        ) : (
          <table className="orders-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 30 }}>#</th>
                <th>Status</th>
                <th>Title</th>
                <th>Category</th>
                <th>Priority</th>
                <th>Submitted By</th>
                <th>Assigned To</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredTickets.map(t => {
                const StatusIcon = STATUS_ICONS[t.status] || AlertCircle;
                const isMyTicket = String(t.assigned_to) === String(user?.id);
                const highlightMine = statusFilter === 'Open' && isMyTicket;
                return (
                  <tr key={t.id} onClick={() => openTicket(t.id)} style={{ cursor: 'pointer', background: highlightMine ? '#fff7ed' : undefined, borderLeft: highlightMine ? '3px solid var(--orange)' : undefined }}>
                    <td style={{ color: 'var(--gray-400)', fontSize: 11 }}>{t.id}</td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 12, background: STATUS_COLORS[t.status] + '18', color: STATUS_COLORS[t.status], fontSize: 11, fontWeight: 600 }}>
                        <StatusIcon size={11} /> {t.status}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600, color: 'var(--navy)' }}>{t.title}</td>
                    <td style={{ fontSize: 12, color: 'var(--gray-600)' }}>{t.category}</td>
                    <td>
                      <span style={{ color: PRIORITY_COLORS[t.priority], fontWeight: 600, fontSize: 12 }}>{t.priority}</span>
                    </td>
                    <td style={{ fontSize: 12 }}>{t.created_by_name}</td>
                    <td style={{ fontSize: 12, color: t.assigned_to_name ? 'var(--navy)' : 'var(--gray-400)' }}>{t.assigned_to_name || 'Unassigned'}</td>
                    <td style={{ fontSize: 11, color: 'var(--gray-500)' }}>{timeSince(t.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  // ── NEW TICKET FORM ──
  if (view === 'new') {
    return (
      <div style={{ padding: '0 8px', maxWidth: 720 }}>
        <button style={{ ...BTN('transparent', false), color: 'var(--navy)', padding: '4px 0', marginBottom: 12 }} onClick={() => setView('list')}>
          <ArrowLeft size={14} /> Back to Tickets
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <AlertTriangle size={20} color="var(--orange)" />
          <h2 style={{ margin: 0, color: 'var(--navy)', fontSize: 18 }}>Report an Issue</h2>
        </div>

        <div className="card" style={{ padding: 20 }}>
          {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>Title *</label>
              <input style={INPUT} placeholder="Brief summary of the issue" value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>Category *</label>
              <select style={INPUT} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>Priority</label>
              <select style={INPUT} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>Assign To *</label>
              <select style={INPUT} value={form.assigned_to || ''} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}>
                <option value="">Select someone...</option>
                {adminUsers.map(u => <option key={u.admin_id} value={u.admin_id}>{u.staff_name}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>Description *</label>
            <textarea style={TEXTAREA} rows={4} placeholder="Describe the issue in detail. What happened? What was the user trying to do?"
              value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>

          {/* Website-specific fields */}
          {form.category === 'Website Issue' && (
            <div style={{ background: 'var(--gray-50)', padding: 14, borderRadius: 8, marginBottom: 12, border: '1px solid var(--gray-200)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 10 }}>Customer / Website Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--gray-600)', marginBottom: 3 }}>Operating System</label>
                  <input style={INPUT} placeholder="e.g. Windows 11, macOS, iOS 17" value={form.customer_os}
                    onChange={e => setForm(f => ({ ...f, customer_os: e.target.value }))} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--gray-600)', marginBottom: 3 }}>Browser</label>
                  <input style={INPUT} placeholder="e.g. Chrome, Safari, Firefox" value={form.customer_browser}
                    onChange={e => setForm(f => ({ ...f, customer_browser: e.target.value }))} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--gray-600)', marginBottom: 3 }}>Additional Info from Customer</label>
                <textarea style={{ ...TEXTAREA, minHeight: 50 }} rows={2} placeholder="Any other details the customer shared (error messages, what page they were on, what they clicked, etc.)"
                  value={form.customer_info} onChange={e => setForm(f => ({ ...f, customer_info: e.target.value }))} />
              </div>
            </div>
          )}

          {/* Screenshot upload */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>Screenshot (optional)</label>
            {screenshotPreview ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={screenshotPreview} alt="Screenshot preview" style={{ maxWidth: 300, maxHeight: 200, borderRadius: 6, border: '1px solid var(--gray-300)' }} />
                <button onClick={removeScreenshot} style={{ position: 'absolute', top: -8, right: -8, width: 22, height: 22, borderRadius: '50%', background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={12} />
                </button>
              </div>
            ) : (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: '1px dashed var(--gray-400)', borderRadius: 6, cursor: 'pointer', color: 'var(--gray-600)', fontSize: 12 }}>
                <Upload size={14} /> Upload Screenshot
                <input type="file" accept="image/*" onChange={handleScreenshot} style={{ display: 'none' }} />
              </label>
            )}
          </div>

          {/* Recommended next steps */}
          <div style={{ marginBottom: 16, background: '#fffbeb', padding: 14, borderRadius: 8, border: '1px solid #fcd34d' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>
              Recommended Next Steps
            </label>
            <p style={{ fontSize: 11, color: '#a16207', margin: '0 0 8px 0' }}>
              What do you think should happen next? Your input helps us solve this faster.
            </p>
            <textarea style={{ ...TEXTAREA, background: '#fff' }} rows={3}
              placeholder="e.g. 'Have George check the checkout API', 'Try clearing browser cache', 'Replace the label printer on the packing line'"
              value={form.recommended_steps} onChange={e => setForm(f => ({ ...f, recommended_steps: e.target.value }))} />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button style={BTN('var(--orange)', saving)} onClick={submitTicket} disabled={saving}>
              <Send size={13} /> {saving ? 'Submitting...' : 'Submit Ticket'}
            </button>
            <button style={{ ...BTN('transparent', false), color: 'var(--gray-600)', border: '1px solid var(--gray-300)' }} onClick={() => setView('list')}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── DETAIL VIEW ──
  if (view === 'detail' && selectedTicket) {
    const t = selectedTicket;
    const StatusIcon = STATUS_ICONS[t.status] || AlertCircle;
    const isClosed = t.status === 'Closed';
    const isResolved = t.status === 'Resolved';
    const isDone = isClosed || isResolved;
    const isSubmitter = String(t.created_by) === String(user?.id);
    const canClose = isSubmitter || isAdmin;

    return (<>
      <div style={{ padding: '0 8px', maxWidth: 800 }}>
        <button style={{ ...BTN('transparent', false), color: 'var(--navy)', padding: '4px 0', marginBottom: 12 }} onClick={() => { setView('list'); loadTickets(); }}>
          <ArrowLeft size={14} /> Back to Tickets
        </button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ color: 'var(--gray-400)', fontSize: 13 }}>#{t.id}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 12, background: STATUS_COLORS[t.status] + '18', color: STATUS_COLORS[t.status], fontSize: 12, fontWeight: 600 }}>
                <StatusIcon size={12} /> {t.status}
              </span>
              <span style={{ color: PRIORITY_COLORS[t.priority], fontWeight: 600, fontSize: 12, padding: '2px 8px', borderRadius: 12, background: PRIORITY_COLORS[t.priority] + '15' }}>
                {t.priority}
              </span>
            </div>
            <h2 style={{ margin: 0, color: 'var(--navy)', fontSize: 18 }}>{t.title}</h2>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
              {t.category} &middot; Submitted by <strong>{t.created_by_name}</strong> on {fmtDate(t.created_at)}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: 16 }}>
          {/* Left column - main content */}
          <div>
            {/* Description */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header" style={{ fontSize: 13, fontWeight: 700 }}>Description</div>
              <div className="card-body" style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6 }}>{t.description}</div>
            </div>

            {/* Website details */}
            {t.category === 'Website Issue' && (t.customer_os || t.customer_browser || t.customer_info) && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header" style={{ fontSize: 13, fontWeight: 700 }}>Customer / Website Details</div>
                <div className="card-body">
                  {(t.customer_os || t.customer_browser) && (
                    <div style={{ display: 'flex', gap: 24, marginBottom: t.customer_info ? 8 : 0 }}>
                      {t.customer_os && <div><span style={{ fontSize: 11, color: 'var(--gray-500)' }}>OS:</span> <strong style={{ fontSize: 12 }}>{t.customer_os}</strong></div>}
                      {t.customer_browser && <div><span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Browser:</span> <strong style={{ fontSize: 12 }}>{t.customer_browser}</strong></div>}
                    </div>
                  )}
                  {t.customer_info && <div style={{ fontSize: 13, marginTop: 4, whiteSpace: 'pre-wrap' }}>{t.customer_info}</div>}
                </div>
              </div>
            )}

            {/* Screenshot */}
            {t.screenshot && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header" style={{ fontSize: 13, fontWeight: 700 }}>Screenshot</div>
                <div className="card-body">
                  <img src={t.screenshot} alt="Issue screenshot" style={{ maxWidth: '100%', borderRadius: 6, cursor: 'pointer' }}
                    onClick={() => setLightboxImg(t.screenshot)} title="Click to view full size" />
                </div>
              </div>
            )}

            {/* Recommended steps */}
            {t.recommended_steps && (
              <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--orange)' }}>
                <div className="card-header" style={{ fontSize: 13, fontWeight: 700, color: 'var(--orange)' }}>Recommended Next Steps</div>
                <div className="card-body" style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{t.recommended_steps}</div>
              </div>
            )}

            {/* Activity timeline */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header" style={{ fontSize: 13, fontWeight: 700 }}>
                <MessageSquare size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
                Activity ({ticketUpdates.length})
              </div>
              <div className="card-body" style={{ padding: ticketUpdates.length ? 0 : undefined }}>
                {ticketUpdates.length === 0 ? (
                  <div style={{ color: 'var(--gray-400)', fontSize: 12 }}>No updates yet.</div>
                ) : (
                  ticketUpdates.map((u, i) => (
                    <div key={u.id} style={{ padding: '10px 16px', borderBottom: i < ticketUpdates.length - 1 ? '1px solid var(--gray-100)' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>
                          {(u.created_by_name || '').split(' ').map(n => n[0]).join('')}
                        </div>
                        <strong style={{ fontSize: 12 }}>{u.created_by_name}</strong>
                        <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>{fmtDateTime(u.created_at)}</span>
                        {u.new_status && (
                          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: STATUS_COLORS[u.new_status] + '18', color: STATUS_COLORS[u.new_status], fontWeight: 600 }}>
                            &rarr; {u.new_status}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--gray-700)', paddingLeft: 28, whiteSpace: 'pre-wrap' }}>{u.note}</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Add note */}
            {(!isClosed && (!isResolved || canClose)) && (
              <div className="card">
                <div className="card-header" style={{ fontSize: 13, fontWeight: 700 }}>Add Update</div>
                <div className="card-body">
                  <textarea style={{ ...TEXTAREA, marginBottom: 8 }} rows={3} placeholder="Add a progress note, findings, or resolution..."
                    value={noteText} onChange={e => setNoteText(e.target.value)} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <select style={{ ...INPUT, width: 'auto', minWidth: 140 }} value={noteStatus} onChange={e => setNoteStatus(e.target.value)}>
                      <option value="">No status change</option>
                      {['In Progress', 'Resolved', 'Closed'].filter(s => s !== t.status && (s !== 'Closed' || canClose)).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button style={BTN('var(--navy)', addingNote || (!noteText.trim() && !noteStatus))} onClick={addNote}
                      disabled={addingNote || (!noteText.trim() && !noteStatus)}>
                      <Send size={12} /> {addingNote ? 'Saving...' : 'Post Update'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Reopen if closed */}
            {isClosed && (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <button style={BTN('var(--navy)', false)} onClick={() => { setNoteStatus('Open'); setNoteText('Reopened ticket'); addNote(); }}>
                  Reopen Ticket
                </button>
              </div>
            )}
          </div>

          {/* Right column - sidebar info */}
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-body" style={{ padding: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>Assigned To</div>
                <select style={{ ...INPUT, marginBottom: 12 }} value={t.assigned_to || ''}
                  onChange={e => assignTicket(e.target.value)}>
                  <option value="">Unassigned</option>
                  {adminUsers.map(u => <option key={u.admin_id} value={u.admin_id}>{u.staff_name}</option>)}
                </select>

                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 2 }}>Created</div>
                <div style={{ fontSize: 12, marginBottom: 10 }}>{fmtDateTime(t.created_at)}</div>

                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 2 }}>Last Updated</div>
                <div style={{ fontSize: 12, marginBottom: 10 }}>{fmtDateTime(t.updated_at)}</div>

                {t.closed_at && (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 2 }}>Closed</div>
                    <div style={{ fontSize: 12 }}>{fmtDateTime(t.closed_at)}</div>
                  </>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-body" style={{ padding: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>Quick Actions</div>
                {!isDone && t.status !== 'In Progress' && (
                  <button style={{ ...BTN('var(--navy)', false), width: '100%', justifyContent: 'center', marginBottom: 6 }}
                    onClick={() => { setNoteStatus('In Progress'); setNoteText('Started working on this'); setTimeout(addNote, 0); }}>
                    <Clock size={12} /> Mark In Progress
                  </button>
                )}
                {!isDone && (
                  <button style={{ ...BTN('#16a34a', false), width: '100%', justifyContent: 'center', marginBottom: 6 }}
                    onClick={() => { setNoteStatus('Resolved'); setNoteText('Issue resolved'); setTimeout(addNote, 0); }}>
                    <CheckCircle size={12} /> Resolve
                  </button>
                )}
                {!isClosed && canClose && (
                  <button style={{ ...BTN('var(--orange)', false), width: '100%', justifyContent: 'center' }}
                    onClick={() => { setNoteStatus('Closed'); setNoteText('Ticket closed'); setTimeout(addNote, 0); }}>
                    <XCircle size={12} /> Close
                  </button>
                )}
                {!isDone && !canClose && (
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', textAlign: 'center', marginTop: 4 }}>
                    Only the submitter can close this ticket.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {lightboxImg && (
        <div onClick={() => setLightboxImg(null)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: 20 }}>
          <img src={lightboxImg} alt="Screenshot full size" style={{ maxWidth: '95%', maxHeight: '95%', borderRadius: 8, boxShadow: '0 4px 40px rgba(0,0,0,0.5)' }} />
          <button onClick={() => setLightboxImg(null)}
            style={{ position: 'absolute', top: 16, right: 16, width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ✕
          </button>
        </div>
      )}
    </>);
  }

  return null;
}
